import { Box, BoxProps, Button, Icon, IconButton, Skeleton, Stack } from '@mui/material';
import {
  Assessment,
  AssessmentBase,
  ASSESSMENTS_FILTER_KEYS,
  DATE_FORMAT_SHORT_FNS,
  FilterKeys,
  GetAssessmentsQueryFilters,
  RecurringState,
  RepetitionType,
  SORT_DIRECTION,
  useGetAssessmentsQuery,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { PageHeaderSearchInput, SearchInputFilterIconButton } from '@schooly/components/filters';
import { RecurringExpandableCard } from '@schooly/components/recurring';
import { useAssessmentLabels } from '@schooly/hooks/use-assessment-labels';
import { useFlag } from '@schooly/hooks/use-flag';
import { useInfiniteScroll } from '@schooly/hooks/use-infinite-scroll';
import {
  CrossIcon,
  EyeIcon,
  GridRowDate,
  GridRowName,
  randomInt,
  RowSelectable,
  RowSimple,
  TypographyWithOverflowHint,
} from '@schooly/style';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { format } from 'date-fns';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { AccessDenied } from '@schooly/style';
import { NoSearchResultsFound } from '../../../../components/common/NoSearchResultsFound/NoSearchResultsFound';
import { ModalPanel } from '../../../../components/uikit-components/Modal/Modal.styled';
import { ModalHeader } from '../../../../components/uikit-components/Modal/ModalHeader';
import useAppLocation from '../../../../hooks/useAppLocation';
import useSchoolYears from '../../../../hooks/useSchoolYears';
import { InlineFiltersTags } from './InlineFiltersTags';
import { SelectAssessmentFiltersPopup } from './SelectAssessmentFiltersPopup';

export interface SelectAssessmentExternalSidebarProps {
  selectedIds: string[];
  assessmentListFilter?: (v: AssessmentBase) => void;
  onCardClick: (group: Assessment) => void;
}

type WithRecurringState<T extends { recurring_state: RecurringState | null }> = Omit<
  T,
  'recurring_state'
> & { recurring_state: RecurringState };

const PAGE_SIZE = 20;

export const SelectAssessmentExternalSidebar: React.FC<SelectAssessmentExternalSidebarProps> = ({
  selectedIds,
  assessmentListFilter,
  onCardClick,
}) => {
  const { formatMessage } = useIntl();
  const { schoolId = '' } = useAuth();
  const { defaultValidity } = useSchoolYears();
  const [filtersModalVisible, openFilterModal, closeFiltersModal] = useFlag(false);

  const defaultFilters: GetAssessmentsQueryFilters = useMemo(() => {
    return {
      [FilterKeys.Date]: [defaultValidity?.start || '', defaultValidity?.end || ''],
      [FilterKeys.RepetitionType]: [RepetitionType.NotRecurring],
    };
  }, [defaultValidity]);

  const {
    data,
    isLoading,
    params,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    setParams,
    error,
  } = useGetAssessmentsQuery(
    {
      school_id: schoolId,
      query: '',
      page_size: PAGE_SIZE,
      sort: [{ columnTextId: 'name', direction: SORT_DIRECTION.ASC }],
      filters: defaultFilters,
    },
    { refetchOnMount: 'always', enabled: !!schoolId },
  );

  const filtersChanged = ASSESSMENTS_FILTER_KEYS.some((key) => {
    const defaultFiltersForKey = [...(defaultFilters[key] || [])];
    const actualFiltersForKey = [...(params.filters[key] || [])];
    return defaultFiltersForKey.sort().join('') !== actualFiltersForKey.sort().join('');
  });

  const handleSetFiltersQuery = useCallback(
    (query: string) => {
      setParams((p) => ({ ...p, query }));
    },
    [setParams],
  );

  const handleResetFilters = useCallback(() => {
    setParams((params) => ({ ...params, filters: defaultFilters }));
  }, [defaultFilters, setParams]);

  const handleSetFilters = useCallback(
    (filters: GetAssessmentsQueryFilters) => {
      setParams((params) => ({ ...params, filters: { ...params.filters, ...filters } }));
    },
    [setParams],
  );

  const total = data?.pages[0]?.count;
  const loaderRef = useInfiniteScroll(
    isLoading || isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    0,
  );

  const entries = useMemo(() => {
    return (
      data?.pages
        .reduce<Assessment[]>((prev, curr) => [...prev, ...curr.results], [])
        // We are moving assessments with recurring state in RecurringAssessmentCard
        .filter(
          (a) =>
            a.recurring_state ||
            (!selectedIds.includes(a.id) &&
              (assessmentListFilter ? assessmentListFilter(a) : true)),
        ) ?? []
    );
  }, [assessmentListFilter, data?.pages, selectedIds]);

  if (error) {
    return <AccessDenied />;
  }

  const placeholder = entries
    ? formatMessage(
        { id: 'reports-SearchAmongCount' },
        {
          reportsPlural: formatMessage({ id: 'reports-Assessments' }).toLowerCase(),
        },
      )
    : undefined;

  return (
    <Stack sx={{ height: '100%' }}>
      <ModalHeader title={formatMessage({ id: 'section-Assessments' })} active />
      <ModalPanel active sx={{ height: '100%', overflow: 'hidden', mb: -2 }}>
        <Stack gap={1}>
          <Stack
            justifyContent="center"
            position="relative"
            sx={{ '&>.MuiBox-root': { maxWidth: 'unset' } }}
          >
            <PageHeaderSearchInput
              value={params.query || ''}
              onChangeText={handleSetFiltersQuery}
              placeholder={placeholder}
              canClear={false}
              InputProps={{
                endAdornment: (
                  <Stack pl={0.75} gap={0.5} pr={1} flexDirection="row">
                    {!!params.query && (
                      <IconButton
                        sx={(theme) => ({ '&:hover': { color: theme.palette.text.primary } })}
                        inverse
                        onClick={() => handleSetFiltersQuery('')}
                      >
                        <CrossIcon />
                      </IconButton>
                    )}
                    <Icon
                      sx={{
                        position: 'relative',
                        '.MuiButtonBase-root': { right: 0 },
                        overflow: 'visible',
                      }}
                    >
                      <SearchInputFilterIconButton hasAppliedIndicator onClick={openFilterModal} />
                    </Icon>
                  </Stack>
                ),
              }}
            />
          </Stack>
          <InlineFiltersTags
            onResetFilters={filtersChanged ? handleResetFilters : undefined}
            onOpenFilters={openFilterModal}
            filters={params.filters}
          />
        </Stack>
        {filtersModalVisible && (
          <SelectAssessmentFiltersPopup
            onClose={closeFiltersModal}
            defaultFilters={defaultFilters}
            onSetFilters={handleSetFilters}
            filters={params.filters}
          />
        )}
        <Stack
          sx={{ overflowY: 'auto', height: '80%', mr: -2, pr: 2, mt: 2 }}
          data-test-id="assessment-list"
        >
          {!isLoading && !entries.length && <NoSearchResultsFound />}
          {entries.map((assessment) =>
            assessment.recurring_state ? (
              <RecurringAssessmentCard
                key={assessment.id}
                assessment={assessment as WithRecurringState<Assessment>}
                schoolId={schoolId}
                onRecurringItemClick={onCardClick}
                selectedIds={selectedIds}
              />
            ) : (
              <AssessmentCard key={assessment.id} assessment={assessment} onClick={onCardClick} />
            ),
          )}
          {isLoading && <AssessmentsListSkeleton rowCount={PAGE_SIZE} />}
          {hasNextPage && (
            <div ref={loaderRef}>
              <AssessmentsListSkeleton
                rowCount={Math.min(
                  PAGE_SIZE,
                  total && data ? total - data.pages.length * PAGE_SIZE : PAGE_SIZE,
                )}
              />
            </div>
          )}
        </Stack>
      </ModalPanel>
    </Stack>
  );
};

type RecurringAssessmentCardProps = {
  onRecurringItemClick: (a: Assessment) => void;
  schoolId: string;
  assessment: WithRecurringState<AssessmentBase>;
  selectedIds: string[];
};

const RecurringAssessmentCard: FC<RecurringAssessmentCardProps> = ({
  onRecurringItemClick,
  schoolId,
  assessment,
  selectedIds,
}) => {
  const [expanded, setExpanded] = useState(false);

  const { data, isFetching, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useGetAssessmentsQuery(
      {
        school_id: schoolId || '',
        page_size: PAGE_SIZE,
        filters: assessment.recurring_state.recurrence_id
          ? { [FilterKeys.RecurrenceId]: [assessment.recurring_state.recurrence_id] }
          : {},
      },
      {
        enabled: Boolean(expanded && assessment.recurring_state.recurrence_id),
        refetchOnMount: 'always',
        onError: () => setExpanded(false),
      },
    );

  const recurringItems = useMemo(
    () => data?.pages.reduce<Assessment[]>((prev, curr) => [...prev, ...curr.results], []) ?? [],
    [data?.pages],
  );

  const filteredRecurringItems = useMemo(() => {
    return recurringItems.filter((a) => !selectedIds.includes(a.id));
  }, [recurringItems, selectedIds]);

  const isEmpty = !filteredRecurringItems.length && !isFetching && !hasNextPage;

  useEffect(() => {
    const isLoadedAssessmentsAdded = Boolean(
      recurringItems.length && !filteredRecurringItems.length && hasNextPage && !isFetchingNextPage,
    );
    if (isLoadedAssessmentsAdded) {
      fetchNextPage();
    }
  }, [
    fetchNextPage,
    filteredRecurringItems.length,
    hasNextPage,
    isFetchingNextPage,
    recurringItems.length,
  ]);

  return (
    <RecurringExpandableCard
      item={assessment}
      onToggle={() => setExpanded((e) => !e)}
      expanded={expanded}
      fetching={isFetching}
    >
      {Boolean(recurringItems.length && expanded) && (
        <>
          <Box>
            {filteredRecurringItems.map((assessment) => (
              <AssessmentCard
                key={assessment.id}
                assessment={assessment}
                onClick={onRecurringItemClick}
              />
            ))}

            {isEmpty && (
              <Stack mt={0.5} mb={1.25} pl={1.5} pr={4.5} alignItems="center">
                <FormattedMessage id="reports-AllRecurringAssessmentsAdded" />
              </Stack>
            )}

            {(isLoading || isFetchingNextPage) && (
              <AssessmentsListSkeleton rowCount={2} mb={1.25} />
            )}
          </Box>

          {hasNextPage && !isFetchingNextPage && (
            <Stack mt={1} mb={1.25} alignItems="center">
              <Button startIcon={<EyeIcon />} variant="text" onClick={() => fetchNextPage()}>
                <FormattedMessage id="action-ShowMoreButton" />
              </Button>
            </Stack>
          )}
        </>
      )}
    </RecurringExpandableCard>
  );
};

type AssessmentCardProps = {
  assessment: Assessment;
  onClick: (assessment: Assessment) => void;
};

const AssessmentCard = ({ assessment, onClick }: AssessmentCardProps) => {
  const location = useAppLocation();

  const { getAssessmentEvaluationTitle } = useAssessmentLabels();
  const evaluationTitle = getAssessmentEvaluationTitle(assessment);

  return (
    <RowSelectable
      noBorderRadius={!assessment.recurring_state}
      onClick={() => onClick(assessment)}
      sx={{ '& .GridRowItem': { gap: 1, py: 2 } }}
    >
      <GridRowDate
        sx={(theme) => ({
          flex: '0 0 30px',
          py: 0.25,
          whiteSpace: 'unset',
          textAlign: 'center',
          verticalAlign: 'middle',
          '.GridRowItem:hover &': { color: theme.palette.common.grey2 },
        })}
      >
        {format(newDateTimezoneOffset(assessment.assessment_date), DATE_FORMAT_SHORT_FNS)}
      </GridRowDate>

      <GridRowName sx={{ overflow: 'hidden' }}>
        <Stack direction="row">
          <TypographyWithOverflowHint
            variant="h3"
            component={Link}
            to={`/assessments/${assessment.id}`}
            state={{
              prevPathname: `${location.pathname}${location.hash ?? ''}`,
              backgroundLocation: location,
            }}
            onClick={(event) => {
              event.stopPropagation();
            }}
            sx={{
              '&:hover': {
                textDecoration: 'underline',
              },

              ...(assessment.area_of_learning
                ? {}
                : {
                    whiteSpace: 'pre-wrap',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }),
            }}
          >
            {assessment.name}
          </TypographyWithOverflowHint>
        </Stack>

        {evaluationTitle && (
          <TypographyWithOverflowHint>{evaluationTitle}</TypographyWithOverflowHint>
        )}
      </GridRowName>
    </RowSelectable>
  );
};

type AssessmentsListSkeletonProps = {
  rowCount: number;
} & BoxProps;

const AssessmentsListSkeleton = ({ rowCount, ...props }: AssessmentsListSkeletonProps) => {
  return (
    <Box {...props}>
      {[...new Array(rowCount)].map((_, i) => {
        return (
          <RowSimple key={i} sx={{ pointerEvents: 'none', '& .GridRowItem': { gap: 1, py: 1.5 } }}>
            <GridRowName>
              {[...new Array(2)].map((_, i) => (
                <Skeleton key={i} width={`${randomInt(50, 100)}%`} />
              ))}
            </GridRowName>
          </RowSimple>
        );
      })}
    </Box>
  );
};
